// Plans, subscriptions, usage (data-model.md "Money, plans, ...").
import { z } from "zod";
import { AccountId, AppId, PlanId, SubscriptionId, UsageCounterId } from "../ids.js";
import { CurrencyCode, MinorUnits } from "../money.js";
import { Timestamp } from "./common.js";
import { PlanName } from "./enums.js";
/** A subscription tier. Entitlements are checked server-side, default-deny, on every gated action. */
export const Plan = z.object({
    id: PlanId,
    name: PlanName,
    /** List price in the plan's currency minor units (integer). */
    price: MinorUnits,
    currency: CurrencyCode,
    /**
     * Included changes per period (integer). The column keeps its name; the CUSTOMER-FACING word is
     * "changes" and never "units" (D98, crew/config/plan-language.json). A unit is an accounting entry
     * and a founder reading it learns nothing about what they are buying.
     */
    includedUnits: z.int().nonnegative(),
    /** Max apps under this plan (integer). */
    appLimit: z.int().positive(),
    /**
     * Price of one extra change beyond the allowance, in the plan's currency minor units. The plan's
     * own effective rate per change plus 50% (D98) - charged over the in-plan rate, never over cost:
     * cost plus 50% comes out below every in-plan rate, which would make going over the plan the
     * cheapest way to buy the product and leave the tier above unsellable.
     */
    overagePriceMinor: MinorUnits,
    /**
     * The fewest changes a month this plan may be sold as, for any app. An app whose calibrated
     * capacity falls below it is routed to the tier above (D98).
     */
    minChanges: z.int().positive(),
    /** The feature set (jsonb). Capability keys the server checks default-deny. */
    entitlements: z.record(z.string(), z.unknown()),
});
/** An account's active subscription. `status` mirrors the billing provider's own set (external vocabulary). */
export const Subscription = z.object({
    id: SubscriptionId,
    accountId: AccountId,
    planId: PlanId,
    stripeSubId: z.string().trim().min(1).nullable(),
    status: z.string().trim().min(1),
    firstMonthDiscount: z.boolean(),
    currentPeriodEnd: Timestamp.nullable(),
});
/**
 * The ONLY usage number a customer sees, expressed in value units, never tokens or
 * cost (00-architecture invariant 8; data-model.md). No cost field exists on this
 * type by construction. `strictObject`: `rowToUsageCounter` (money.service.ts) parses
 * a column-specific select by explicit field copy, so a hostile/future cost column on
 * the row fails the boundary parse rather than riding along - the same loud-reject D27
 * posture as the tenant read views.
 */
export const UsageCounter = z.strictObject({
    id: UsageCounterId,
    accountId: AccountId,
    appId: AppId,
    /** The billing period this counter covers (e.g. an ISO month). */
    period: z.string().trim().min(1),
    valueUnitsUsed: z.int().nonnegative(),
    valueUnitsIncluded: z.int().nonnegative(),
    overageUnits: z.int().nonnegative(),
    /**
     * Changes the platform did and did not charge for: rework the crew caused, and the overrun on a
     * quote the crew wrote too small (D99). A COUNT, never a cost - which is what lets it sit on this
     * type at all under invariant 8. It is customer-facing on purpose: it is the evidence behind "if
     * we get a change wrong, fixing it is on us, and it does not come out of your month", and a
     * promise with a number beside it is worth more than the same promise in a FAQ.
     */
    absorbedUnits: z.int().nonnegative(),
});
//# sourceMappingURL=billing.js.map